import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ClientAvatar } from "@/components/ClientAvatar";
import { formatCurrency, formatDate } from "@/lib/constants";
import { Loader2, Mail, Phone, Search, Users, ChevronRight } from "lucide-react";
import { toast } from "sonner";

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("recent");
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/clients")
      .then(({ data }) => setClients(data))
      .catch((err) => toast.error(apiError(err)))
      .finally(() => setLoading(false));
  }, []);

  const SORTS = {
    recent: { label: "Most recent order", fn: (a, b) => (b.last_order_at || "").localeCompare(a.last_order_at || "") },
    name: { label: "Name (A–Z)", fn: (a, b) => (a.name || "").localeCompare(b.name || "") },
    orders: { label: "Most orders", fn: (a, b) => (b.orders_count || 0) - (a.orders_count || 0) },
    spend: { label: "Highest lifetime spend", fn: (a, b) => (b.total_spend || 0) - (a.total_spend || 0) },
  };

  const filtered = clients.filter((c) =>
    (c.name || "").toLowerCase().includes(q.toLowerCase()) ||
    (c.email || "").toLowerCase().includes(q.toLowerCase())
  ).sort(SORTS[sort].fn);

  return (
    <div>
      <PageHeader title="Clients" subtitle={`${clients.length} agents & brokerages`} testid="clients-header" />
      <div className="space-y-5 p-6 md:p-8">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="relative sm:max-w-xs sm:flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search clients…" className="pl-9" data-testid="clients-search" />
          </div>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="sm:w-56" data-testid="clients-sort">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(SORTS).map(([k, v]) => (
                <SelectItem key={k} value={k} data-testid={`clients-sort-${k}`}>{v.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : filtered.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border py-16 text-center">
            <Users className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">No clients yet. Sync your Aryeo orders to populate this list.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((c, i) => (
              <button key={c.id || c.email || i} onClick={() => navigate(`/clients/${encodeURIComponent(c.id)}`)}
                className="group rounded-xl border border-border bg-card p-5 text-left transition-transform duration-200 hover:-translate-y-1 hover:border-primary/40"
                data-testid={`client-card-${i}`}>
                <div className="flex items-center gap-3">
                  <ClientAvatar name={c.name} email={c.email} className="h-11 w-11 text-sm" testId={`client-card-avatar-${i}`} />
                  {c.logo && <img src={c.logo} alt={`${c.name} logo`} className="h-11 w-11 shrink-0 rounded-md bg-secondary object-contain p-1" data-testid={`client-card-logo-${i}`} />}
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-medium">{c.name}</div>
                    <div className="truncate text-xs text-muted-foreground">Last order {formatDate(c.last_order_at)}</div>
                  </div>
                  <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-primary" />
                </div>
                <div className="mt-4 space-y-1.5 text-sm">
                  {c.email && <div className="flex items-center gap-2 text-muted-foreground"><Mail className="h-3.5 w-3.5" /><span className="truncate">{c.email}</span></div>}
                  {c.phone && <div className="flex items-center gap-2 text-muted-foreground"><Phone className="h-3.5 w-3.5" />{c.phone}</div>}
                </div>
                <div className="mt-4 flex items-center justify-between border-t border-border pt-3">
                  <div>
                    <div className="font-heading text-lg font-bold">{c.orders_count}</div>
                    <div className="overline text-muted-foreground">Orders</div>
                  </div>
                  <div className="text-right">
                    <div className="font-heading text-lg font-bold">{formatCurrency(c.total_spend, c.currency)}</div>
                    <div className="overline text-muted-foreground">Lifetime</div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
